<template>
    <div class="bsa-table-card">
        <div class="bsa-table-head">
            <span class="bsa-table-title">Statement of financial position</span>
            <button v-if="groupKeys.length" type="button" class="bsa-table-link" @click="toggleAll">
                <span :key="linkIcon"><i :class="linkIcon"></i></span>
                {{ allExpanded ? 'Collapse all' : 'Expand all' }}
            </button>
        </div>
        <EmptyState
            v-if="showEmpty"
            icon="fa-solid fa-scale-balanced"
            title="Nothing loaded yet"
            message="Choose a date range and press Go to load the report."
            compact
        />
        <table v-else-if="showSkeleton" class="bsa-table">
            <tbody>
                <TableSkeleton :rows="8" :columns="6" />
            </tbody>
        </table>
        <div v-else class="bsa-table-wrap" :class="{ 'is-reloading': loading }">
            <table class="bsa-table">
                <thead>
                <tr>
                    <th class="bsa-sticky">Account Head</th>
                    <th v-for="column in columns" :key="column.key" class="num">{{ column.label }}</th>
                    <th class="num bsa-total-col">{{ totalLabel }}</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="section in sections" :key="section.key">
                    <tr class="bsa-section" :class="`is-${section.key}`">
                        <td class="bsa-sticky">{{ section.title }}</td>
                        <td v-for="column in columns" :key="column.key"></td>
                        <td></td>
                    </tr>
                    <tr v-for="row in flattenNodes(section.data, 1, section.key)" :key="row.key" :class="`bsa-row-${row.kind}`">
                        <td class="bsa-sticky">
                            <span class="bsa-name" :style="{ paddingLeft: (row.depth * 16) + 'px' }">
                                <button
                                    v-if="row.kind === 'group'"
                                    type="button"
                                    class="bsa-chevron"
                                    :class="{ 'is-collapsed': row.collapsed }"
                                    @click="toggleGroup(row.key)"
                                >
                                    <i class="fa-solid fa-chevron-down"></i>
                                </button>
                                {{ row.name }}
                            </span>
                        </td>
                        <template v-if="row.showAmounts">
                            <td v-for="column in columns" :key="column.key" class="num" :class="amountClass(columnValue(row, column.key))">
                                {{ commaFormat(columnValue(row, column.key), decimals) }}
                            </td>
                            <td class="num bsa-total-col" :class="amountClass(row.total)">{{ commaFormat(row.total, decimals) }}</td>
                        </template>
                        <template v-else>
                            <td v-for="column in columns" :key="column.key"></td>
                            <td class="bsa-total-col"></td>
                        </template>
                    </tr>
                    <tr class="bsa-section-total" :class="`is-${section.key}`">
                        <td class="bsa-sticky">TOTAL {{ section.title }}</td>
                        <td v-for="column in columns" :key="column.key" class="num" :class="amountClass(section.totals.columns[column.key])">
                            {{ commaFormat(section.totals.columns[column.key], decimals) }}
                        </td>
                        <td class="num bsa-total-col" :class="amountClass(section.totals.total)">{{ commaFormat(section.totals.total, decimals) }}</td>
                    </tr>
                </template>
                <tr class="bsa-summary-row">
                    <td class="bsa-sticky">TOTAL LIABILITIES AND EQUITY</td>
                    <td v-for="column in columns" :key="column.key" class="num" :class="amountClass(liabilityAndEquity.columns[column.key])">
                        {{ commaFormat(liabilityAndEquity.columns[column.key], decimals) }}
                    </td>
                    <td class="num bsa-total-col" :class="amountClass(liabilityAndEquity.total)">{{ commaFormat(liabilityAndEquity.total, decimals) }}</td>
                </tr>
                <tr class="bsa-summary-row">
                    <td class="bsa-sticky">DIFFERENCE (ASSETS - LIABILITIES AND EQUITY)</td>
                    <td v-for="column in columns" :key="column.key" class="num" :class="balanceClass(difference.columns[column.key])">
                        {{ commaFormat(difference.columns[column.key], decimals) }}
                    </td>
                    <td class="num bsa-total-col" :class="balanceClass(difference.total)">{{ commaFormat(difference.total, decimals) }}</td>
                </tr>
                <tr class="bsa-summary-row">
                    <td class="bsa-sticky">LIABILITIES TO EQUITY (%)</td>
                    <td v-for="column in columns" :key="column.key" class="num">
                        {{ formatPercent(liabilityTotals.columns[column.key], equityTotals.columns[column.key]) }}
                    </td>
                    <td class="num bsa-total-col">{{ formatPercent(liabilityTotals.total, equityTotals.total) }}</td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import EmptyState from '@/components/atom/EmptyState.vue'
import TableSkeleton from '@/components/atom/TableSkeleton.vue'

const props = defineProps({
    asset: { type: Array, default: () => [] },
    equity: { type: Array, default: () => [] },
    liability: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    noOfDecimals: { type: [Number, String], default: 2 },
    showAccountCode: { type: Boolean, default: false },
    totalLabel: { type: String, default: 'Total' },
    loading: { type: Boolean, default: false }
})

const { commaFormat } = figureFormatter()

const collapsedKeys = ref(new Set())

const decimals = computed(() => {
    const value = parseInt(props.noOfDecimals)
    return isNaN(value) || value < 0 ? 2 : value
})

const showSkeleton = computed(() => props.loading && !props.columns.length)
const showEmpty = computed(() => !props.loading && !props.columns.length)

const assetTotals = computed(() => sumNodes(props.asset))
const equityTotals = computed(() => sumNodes(props.equity))
const liabilityTotals = computed(() => sumNodes(props.liability))

const sections = computed(() => [
    { key: 'asset', title: 'ASSETS', data: props.asset, totals: assetTotals.value },
    { key: 'equity', title: 'EQUITY', data: props.equity, totals: equityTotals.value },
    { key: 'liability', title: 'LIABILITIES', data: props.liability, totals: liabilityTotals.value }
])

const liabilityAndEquity = computed(() => combineTotals(liabilityTotals.value, equityTotals.value, 1))
const difference = computed(() => combineTotals(assetTotals.value, liabilityAndEquity.value, -1))

const columnValue = (node, key) => {
    if (!node.columns) return 0
    return node.columns[key] || 0
}

const sumNodes = (nodes) => {
    const columns = {}
    props.columns.forEach(column => {
        columns[column.key] = nodes.reduce((sum, node) => sum + columnValue(node, column.key), 0)
    })
    return {
        columns,
        total: nodes.reduce((sum, node) => sum + (node.total || 0), 0)
    }
}

const combineTotals = (first, second, sign) => {
    const columns = {}
    props.columns.forEach(column => {
        columns[column.key] = first.columns[column.key] + sign * second.columns[column.key]
    })
    return {
        columns,
        total: first.total + sign * second.total
    }
}

const accountLabel = (node) => {
    if (!props.showAccountCode || !node.account_code) return node.name
    return `${node.account_code} - ${node.name}`
}

const nodeKey = (parentKey, node, index) => `${parentKey}/${index}-${node.name}`

const collectGroupKeys = (nodes, parentKey) => {
    if (!Array.isArray(nodes)) return []
    return nodes.reduce((keys, node, index) => {
        if (node.type !== 'group') return keys
        const key = nodeKey(parentKey, node, index)
        return keys.concat([key], collectGroupKeys(node.child, key))
    }, [])
}

const groupKeys = computed(() => sections.value.reduce((keys, section) => keys.concat(collectGroupKeys(section.data, section.key)), []))
const allExpanded = computed(() => !groupKeys.value.some(key => collapsedKeys.value.has(key)))
const linkIcon = computed(() => allExpanded.value ? 'fa-solid fa-compress' : 'fa-solid fa-expand')

const flattenNodes = (nodes, depth, parentKey) => {
    if (!Array.isArray(nodes)) return []
    return nodes.reduce((rows, node, index) => rows.concat(nodeRows(node, depth, nodeKey(parentKey, node, index))), [])
}

const nodeRows = (node, depth, key) => {
    const row = { key, name: accountLabel(node), depth, columns: node.columns, total: node.total, kind: 'endpoint', collapsed: false, showAmounts: true }
    if (node.type !== 'group') return [row]
    const collapsed = collapsedKeys.value.has(key)
    const groupRow = { ...row, kind: 'group', collapsed, showAmounts: collapsed }
    if (collapsed) return [groupRow]
    return [groupRow]
        .concat(flattenNodes(node.child, depth + 1, key))
        .concat([{ ...row, key: `${key}/total`, name: `Total ${node.name}`, kind: 'total' }])
}

const toggleGroup = (key) => {
    const keys = new Set(collapsedKeys.value)
    if (keys.has(key)) {
        keys.delete(key)
        collapsedKeys.value = keys
        return
    }
    keys.add(key)
    collapsedKeys.value = keys
}

const toggleAll = () => {
    collapsedKeys.value = allExpanded.value ? new Set(groupKeys.value) : new Set()
}

const amountClass = (value) => (value || 0) < 0 ? 'is-negative' : ''

const balanceClass = (value) => Math.abs(value || 0) >= 0.01 ? 'is-unbalanced' : 'is-balanced'

const formatPercent = (value, base) => {
    if (!value || !base) return '-'
    return ((value / base) * 100).toFixed(2) + '%'
}
</script>

<style scoped>
.bsa-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    overflow: hidden;
}
.bsa-table-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #eaecf0;
}
.bsa-table-title {
    font-size: 14px;
    font-weight: 600;
    color: #101828;
}
.bsa-table-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: transparent;
    font-size: 13px;
    font-weight: 500;
    color: #7367f0;
    cursor: pointer;
}
.bsa-table-wrap {
    overflow-x: auto;
    transition: opacity .15s ease;
}
.bsa-table-wrap.is-reloading {
    opacity: .5;
}
.bsa-table {
    width: 100%;
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
}
.bsa-table thead th {
    z-index: 2;
    padding: 10px 12px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #344054;
    white-space: nowrap;
}
.bsa-table tbody td {
    padding: 8px 12px !important;
    border-bottom: 1px solid #f2f4f7;
    background: #fff;
    font-size: 13px;
    color: #101828;
    white-space: nowrap;
}
.bsa-table tbody tr:hover td {
    background: #f9fafb;
}
.bsa-table .num {
    min-width: 110px;
    text-align: right;
    font-variant-numeric: tabular-nums;
}
.bsa-table .bsa-sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 280px;
    max-width: 380px;
    border-right: 1px solid #eaecf0;
}
.bsa-table thead th.bsa-sticky {
    z-index: 3;
}
.bsa-table .bsa-total-col {
    font-weight: 600;
    border-left: 1px solid #eaecf0;
}
.bsa-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: normal;
}
.bsa-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    font-size: 10px;
    color: #667085;
    cursor: pointer;
    transition: transform .15s ease;
}
.bsa-chevron:hover {
    background: #f2f4f7;
}
.bsa-chevron.is-collapsed {
    transform: rotate(-90deg);
}
.bsa-row-group td {
    font-weight: 600;
}
.bsa-row-total td {
    background: #f9fafb;
    font-weight: 600;
}
.bsa-section td {
    padding-top: 12px !important;
    font-size: 12px !important;
    font-weight: 700;
    letter-spacing: .06em;
}
.bsa-section-total td {
    font-weight: 700;
}
.bsa-section.is-asset td,
.bsa-section-total.is-asset td {
    background: #eff8ff;
}
.bsa-section.is-equity td,
.bsa-section-total.is-equity td {
    background: #f4f3ff;
}
.bsa-section.is-liability td,
.bsa-section-total.is-liability td {
    background: #fffaeb;
}
.bsa-section.is-asset td.bsa-sticky {
    box-shadow: inset 3px 0 0 #1570ef;
    color: #175cd3;
}
.bsa-section.is-equity td.bsa-sticky {
    box-shadow: inset 3px 0 0 #7367f0;
    color: #5925dc;
}
.bsa-section.is-liability td.bsa-sticky {
    box-shadow: inset 3px 0 0 #dc6803;
    color: #b54708;
}
.bsa-summary-row td {
    background: #f4f3ff;
    border-top: 1px solid #e0ddff;
    font-weight: 700;
}
.bsa-table td.is-negative {
    color: #d92d20;
}
.bsa-table td.is-unbalanced {
    color: #d92d20;
}
.bsa-table td.is-balanced {
    color: #039855;
}
</style>
